/**
 * useCubeDeltas — diff territory cubeCounts across consecutive `gameState`
 * snapshots and surface deltas as transient events.
 *
 * Why a hook instead of a store middleware? The deltas are pure presentation
 * — no other code in the app cares about them, and they shouldn't survive
 * remounts (a player navigating away and back doesn't need a backlog of
 * stale +N indicators). Keeping the diff logic local to a hook keeps the
 * responsibility next to the component that visualises it.
 *
 * Behaviour:
 *   - On the first `gameState` we observe, we seed the cube-count snapshot
 *     and emit nothing. There's no prior frame to diff against, so emitting
 *     would be a lie about something just happening.
 *   - On every subsequent change of `gameState` identity, we walk every
 *     territory and emit `{ delta }` events for any whose cubeCount moved.
 *   - Events carry a unique id so React keying works under repeated deltas
 *     on the same territory (e.g. a chain of attacks resolving in one
 *     server frame).
 *   - The caller `dismiss(id)`es each event when its <CubeDelta> animation
 *     finishes. The hook holds events until dismissed; it never auto-prunes,
 *     so a forgotten event would leak. The companion `<CubeDelta>` always
 *     fires `onComplete` (with a safety timeout), so this is fine in
 *     practice.
 *
 * No throttling: a typical turn produces O(few) deltas; the cost of
 * Object.values + a Map.get loop over ~60 territories is trivial relative
 * to the React render that already happens on every gameState frame.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useGameState } from '../stores/gameStore';

export interface CubeDeltaEvent {
  readonly id: string;
  readonly territoryId: string;
  readonly delta: number;
  readonly bornAt: number;
}

export interface UseCubeDeltasResult {
  readonly deltas: readonly CubeDeltaEvent[];
  readonly dismiss: (id: string) => void;
}

export function useCubeDeltas(): UseCubeDeltasResult {
  const gameState = useGameState();

  // Track the previous cubeCounts. On first observation the ref is null;
  // we seed it without emitting so the player doesn't see "+5" pop on every
  // territory at session start.
  const lastCubesRef = useRef<Map<string, number> | null>(null);

  // Monotonic counter for unique event ids. Lives in a ref so we don't
  // re-render the hook just to bump the counter.
  const counterRef = useRef<number>(0);
  const lastSeqRef = useRef<number>(-1);

  const [deltas, setDeltas] = useState<readonly CubeDeltaEvent[]>([]);

  useEffect(() => {
    if (!gameState) {
      // GameState cleared (detach / disconnect): drop the snapshot so a
      // fresh attach starts cleanly without phantom deltas.
      lastCubesRef.current = null;
      lastSeqRef.current = -1;
      return;
    }

    if (gameState.seq <= lastSeqRef.current) return;
    lastSeqRef.current = gameState.seq;

    const next = new Map<string, number>();
    for (const t of Object.values(gameState.territories)) {
      next.set(t.territoryId, t.cubeCount);
    }

    const prev = lastCubesRef.current;
    if (prev === null) {
      // First snapshot — seed and emit nothing.
      lastCubesRef.current = next;
      return;
    }

    const newEvents: CubeDeltaEvent[] = [];
    const bornAt = Date.now();
    for (const [id, cur] of next) {
      const before = prev.get(id);
      if (before === undefined) continue; // brand-new territory; treat as initial
      if (before === cur) continue;
      const delta = cur - before;
      counterRef.current += 1;
      newEvents.push({
        id: `${id}-${counterRef.current}`,
        territoryId: id,
        delta,
        bornAt,
      });
    }

    lastCubesRef.current = next;

    if (newEvents.length > 0) {
      setDeltas((prevList) => [...prevList, ...newEvents]);
    }
  }, [gameState]);

  const dismiss = useCallback((id: string) => {
    setDeltas((prev) => prev.filter((d) => d.id !== id));
  }, []);

  return { deltas, dismiss };
}
